import * as React from "react";
import { Check, ChevronsUpDown, AlertCircle, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";

export interface SalesforceField {
    name: string;
    label: string;
    type: string;
    description: string;
    required: boolean;
    picklistValues?: Array<{ value: string; label?: string; active: boolean }>;
}

interface SalesforceFieldSelectProps {
    fields: SalesforceField[];
    value?: string;
    onSelect: (value: string) => void;
    placeholder?: string;
    simpleDisplay?: boolean;
}

interface FieldWarning {
    field: SalesforceField;
    selectedFormField?: string;
}

function getFieldWarningMessage(field: SalesforceField): string | null {
    switch (field.type) {
        case 'picklist':
            const validValues = field.picklistValues
                ?.filter(pv => pv.active)
                .map(pv => pv.value)
                .join(', ');
            return `This is a picklist field. Valid values are: ${validValues}`;
        case 'multipicklist':
            const multiValidValues = field.picklistValues
                ?.filter(pv => pv.active)
                .map(pv => pv.value)
                .join(', ');
            return `This is a multi-select field. Multiple values can be selected from: ${multiValidValues}`;
        case 'boolean':
            return "This field only accepts true/false values";
        case 'date':
            return "Date must be in YYYY-MM-DD format";
        case 'datetime':
            return "DateTime must be in YYYY-MM-DDThh:mm:ssZ format";
        case 'currency':
        case 'double':
        case 'int':
            return "This field must be a number";
        case 'email':
            return "Must be a valid email address";
        case 'url':
            return "Must be a valid URL (starting with http:// or https://)";
        case 'string':
        case 'textarea':
        case 'text':
            return null;
        default:
            return null;
    }
}

export function FieldWarningMessage({ field }: FieldWarning) {
    const warningMessage = getFieldWarningMessage(field);
    return (
        <>
            {warningMessage && (
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger>
                            <AlertCircle className="h-4 w-4 text-amber-500" />
                        </TooltipTrigger>
                        <TooltipContent>
                            <div className="max-w-xs">{warningMessage}</div>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            )}
        </>
    );
}

export function SalesforceFieldSelect({ fields, value, onSelect, placeholder = "Select a field...", simpleDisplay = false }: SalesforceFieldSelectProps) {
    const [open, setOpen] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState("");
    const inputRef = React.useRef<HTMLInputElement>(null);

    const selectedField = fields.find(field => field.name === value);
    const warningMessage = selectedField && getFieldWarningMessage(selectedField);

    const filteredFields = fields.filter(field => 
        field.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        field.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Group fields by type for better organization
    const groupedFields = React.useMemo(() => {
        const groups: Record<string, SalesforceField[]> = {};
        filteredFields.forEach(field => {
            const groupKey = field.required ? 'Required' : 'Optional';
            if (!groups[groupKey]) groups[groupKey] = [];
            groups[groupKey].push(field);
        });
        return groups;
    }, [filteredFields]);

    React.useEffect(() => {
        if (open && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    }, [open]);

    return (
        <div className="space-y-2">
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className={cn(
                            "w-full justify-between h-9 font-normal",
                            !selectedField && "text-muted-foreground"
                        )}
                    >
                        {selectedField ? (
                            <div className="flex items-center gap-2 truncate">
                                <span className="truncate">{selectedField.label}</span>
                                {!simpleDisplay && (
                                    <Badge variant="secondary" className="text-[10px] flex-shrink-0">
                                        {selectedField.type}
                                    </Badge>
                                )}
                                {warningMessage && (
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <span
                                                    className="inline-flex flex-shrink-0 items-center"
                                                    aria-label={warningMessage}
                                                >
                                                    <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
                                                </span>
                                            </TooltipTrigger>
                                            <TooltipContent className="z-[70]">
                                                <div className="max-w-xs text-xs">{warningMessage}</div>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                )}
                            </div>
                        ) : (
                            <span>{placeholder}</span>
                        )}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent 
                    className="p-0 w-[--radix-popover-trigger-width]" 
                    align="start" 
                    sideOffset={4}
                    onWheel={(e) => e.stopPropagation()}
                >
                    <div className="flex flex-col">
                        <div className="p-2 border-b">
                            <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    ref={inputRef}
                                    placeholder="Search fields..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="h-8 pl-8"
                                />
                            </div>
                        </div>
                        <ScrollArea className="h-[280px]">
                            {filteredFields.length === 0 ? (
                                <div className="py-6 text-center text-sm text-muted-foreground">
                                    No fields found
                                </div>
                            ) : (
                                <div className="p-1">
                                    {Object.entries(groupedFields).map(([groupName, groupFields]) => (
                                        <div key={groupName}>
                                            <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground sticky top-0 bg-popover">
                                                {groupName} ({groupFields.length})
                                            </div>
                                            {groupFields.map((field) => (
                                                <button
                                                    key={field.name}
                                                    onClick={() => {
                                                        onSelect(field.name);
                                                        setOpen(false);
                                                        setSearchQuery("");
                                                    }}
                                                    className={cn(
                                                        "w-full flex items-center gap-2 px-2 py-2 text-left rounded-md transition-colors",
                                                        "hover:bg-accent focus:bg-accent focus:outline-none",
                                                        field.name === value && "bg-accent"
                                                    )}
                                                >
                                                    <div className={cn(
                                                        "h-4 w-4 rounded flex items-center justify-center flex-shrink-0",
                                                        field.name === value ? "text-primary" : "text-transparent"
                                                    )}>
                                                        <Check className="h-3.5 w-3.5" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm font-medium truncate">{field.label}</span>
                                                            {field.required && (
                                                                <Badge variant="destructive" className="text-[9px] px-1 py-0">
                                                                    Required
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground truncate">
                                                            <span className="font-mono">{field.name}</span>
                                                            <span>•</span>
                                                            <span className="capitalize">{field.type}</span>
                                                        </div>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </ScrollArea>
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
}
